/**
 * ChatGPT Tax calculator — the headline metric.
 *
 * Two outputs:
 *
 * **Tax Paid** — total time already wasted across the archive (historical fact).
 * **Projected** — based on recent usage patterns, how much you'll waste going forward.
 *
 * Three-loss framework:
 *
 * 1. **The Drag** (render tax): Browser DOM reflow overhead from fat conversations.
 * 2. **Stretch Marks** (re-instruction + topic restart): Time re-explaining things
 *    the model forgot (2.5 min each) + context re-establishment in new chats (7.5 min each extra).
 * 3. **Spare Tire**: Time lost to messages the model didn't process (1.5 min each).
 *
 * Projection uses a recent window (default 30 days from archive end) to capture
 * current habits rather than averaging over the full archive span.
 *
 * @module chatgpt-tax
 */

/** @typedef {import('./types.js').ConversationAnalysis} ConversationAnalysis */
/** @typedef {import('./types.js').CrossChatAnalysis} CrossChatAnalysis */
/** @typedef {import('./types.js').ChatGPTTaxSummary} ChatGPTTaxSummary */

/** Time cost per re-instruction instance (seconds) — typing + reading confused response */
const REINSTRUCTION_COST_SEC = 150; // 2.5 minutes

/** Time cost per extra conversation in a topic restart group (seconds) — context re-establishment */
const RESTART_COST_SEC = 450; // 7.5 minutes

/** Time cost per unheard user message (seconds) — rethinking + resubmitting */
const CONTEXT_WASTE_COST_SEC = 90; // 1.5 minutes

/** Average days per month for extrapolation */
const DAYS_PER_MONTH = 30.44;

/** Average days per year for extrapolation */
const DAYS_PER_YEAR = 365.25;

/** Default recent window size (days) */
const RECENT_WINDOW_DAYS = 30;

/**
 * Calculate the ChatGPT Tax for an entire archive.
 *
 * @param {Object} options
 * @param {ConversationAnalysis[]} options.conversations - All analyzed conversations
 * @param {CrossChatAnalysis} options.crossChat - Cross-chat analysis results
 * @param {number} [options.recentWindowDays=30] - Recent window size for projection
 * @returns {ChatGPTTaxSummary}
 */
export function calculateChatGPTTax({ conversations, crossChat, recentWindowDays = RECENT_WINDOW_DAYS }) {
  // --- Archive time span ---
  let earliest = Infinity;
  let latest = -Infinity;
  for (const conv of conversations) {
    if (conv.createTime > 0 && conv.createTime < earliest) earliest = conv.createTime;
    if (conv.updateTime > 0 && conv.updateTime > latest) latest = conv.updateTime;
  }
  const archiveSpanDays = earliest < Infinity && latest > -Infinity
    ? Math.max(1, (latest - earliest) / 86400)
    : 1;

  // --- Tax Paid: total historical waste ---
  const taxPaid = computeTaxComponents(conversations, crossChat);

  // --- Projected: recent window rate → extrapolate ---
  const windowCutoff = latest > -Infinity
    ? latest - recentWindowDays * 86400
    : 0;
  const actualWindowDays = latest > -Infinity
    ? Math.min(recentWindowDays, archiveSpanDays)
    : 1;

  // Filter to conversations active in the recent window
  const recentConvs = conversations.filter(c => c.updateTime >= windowCutoff);

  // For render tax: only count turn pairs whose assistant timestamp falls in the window
  let recentRenderSeconds = 0;
  for (const conv of recentConvs) {
    for (const tp of conv.turnPairs) {
      if (!tp.perceivedWait) continue;
      const asstTimestamp = conv.messages[tp.assistantIndex]?.timestamp;
      if (asstTimestamp && asstTimestamp >= windowCutoff) {
        recentRenderSeconds += tp.perceivedWait.renderDelay;
      } else if (!asstTimestamp) {
        // No timestamp — include if the conversation is recent (conservative)
        recentRenderSeconds += tp.perceivedWait.renderDelay;
      }
    }
  }

  // For reinstruction tax: count reinstructions from recently active conversations
  let recentReinstructions = 0;
  for (const conv of recentConvs) {
    if (conv.reinstruction) {
      recentReinstructions += conv.reinstruction.reinstructionCount;
    }
  }

  // For topic restart tax: count restart groups with recent activity
  let recentExtraRestarts = 0;
  const recentConvIds = new Set(recentConvs.map(c => c.conversationId));
  for (const group of crossChat.topicRestarts.restartGroups) {
    const recentInGroup = group.conversationIds.filter(id => recentConvIds.has(id)).length;
    if (recentInGroup > 1) {
      recentExtraRestarts += recentInGroup - 1;
    }
  }

  // For context waste tax: count wasted messages from recently active conversations
  let recentWastedMessages = 0;
  for (const conv of recentConvs) {
    if (conv.contextWaste) {
      recentWastedMessages += conv.contextWaste.wastedMessageCount;
    }
  }

  const recentRenderMin = recentRenderSeconds / 60;
  const recentReinstructionMin = (recentReinstructions * REINSTRUCTION_COST_SEC) / 60;
  const recentRestartMin = (recentExtraRestarts * RESTART_COST_SEC) / 60;
  const recentContextWasteMin = (recentWastedMessages * CONTEXT_WASTE_COST_SEC) / 60;
  const recentTotalMin = recentRenderMin + recentReinstructionMin + recentRestartMin + recentContextWasteMin;

  const dailyRate = recentTotalMin / actualWindowDays;

  const projected = {
    dailyRate,
    weekMinutes: dailyRate * 7,
    weekHours: (dailyRate * 7) / 60,
    monthMinutes: dailyRate * DAYS_PER_MONTH,
    monthHours: (dailyRate * DAYS_PER_MONTH) / 60,
    yearMinutes: dailyRate * DAYS_PER_YEAR,
    yearHours: (dailyRate * DAYS_PER_YEAR) / 60,
    recentWindowDays: actualWindowDays,
    recentRenderMinutes: recentRenderMin,
    recentReinstructionMinutes: recentReinstructionMin,
    recentRestartMinutes: recentRestartMin,
    recentContextWasteMinutes: recentContextWasteMin,
    recentTotalMinutes: recentTotalMin,
  };

  // --- Bloat distribution ---
  let green = 0;
  let yellow = 0;
  let red = 0;
  for (const conv of conversations) {
    if (conv.bloatScore < 0.5) green++;
    else if (conv.bloatScore < 1.0) yellow++;
    else red++;
  }
  const fatChatCount = yellow + red;

  return {
    taxPaid,
    projected,
    archiveSpanDays,
    fatChatCount,
    bloatDistribution: { green, yellow, red },
  };
}

/**
 * Normalization ceilings — define what "1.0" means for each loss component.
 * Values at or above the ceiling normalize to 1.0.
 */
const DRAG_CEILING_MIN = 10;     // 10 min cumulative render delay = fully dragged
const STRETCH_CEILING_MIN = 30;  // 30 min reinstruction + restart cost = fully stretched
const SPARE_CEILING_MIN = 15;    // 15 min wasted message cost = fully wasted

/** Component weights (must sum to 1) */
const WEIGHT_DRAG = 1 / 3;
const WEIGHT_STRETCH = 1 / 3;
const WEIGHT_SPARE = 1 / 3;

/**
 * Compute per-chat tax score for a single conversation.
 *
 * Each loss component is computed in minutes then normalized to 0–1:
 * - **Drag score**: renderDelayMinutes / DRAG_CEILING
 * - **Stretch score**: (reinstructionMin + restartMin) / STRETCH_CEILING
 * - **Spare score**: wasteMin / SPARE_CEILING
 *
 * Composite: dragScore × W_drag + stretchScore × W_stretch + spareScore × W_spare
 *
 * Result is 0–1, mapped to wash categories by threshold.
 *
 * @param {ConversationAnalysis} conv
 * @param {CrossChatAnalysis} crossChat
 * @returns {{ score: number, dragScore: number, stretchScore: number, spareScore: number, dragMin: number, stretchMin: number, spareMin: number }}
 */
export function computePerChatTax(conv, crossChat) {
  // The Drag: measured render overhead
  let renderSec = 0;
  for (const tp of conv.turnPairs) {
    if (tp.perceivedWait) {
      renderSec += tp.perceivedWait.renderDelay;
    }
  }
  const dragMin = renderSec / 60;

  // Stretch Marks: re-instruction cost + topic restart cost
  const reinstructionMin = conv.reinstruction
    ? (conv.reinstruction.reinstructionCount * REINSTRUCTION_COST_SEC) / 60
    : 0;

  let restartSec = 0;
  for (const group of crossChat.topicRestarts.restartGroups) {
    if (group.conversationIds.includes(conv.conversationId)) {
      const extras = group.count - 1;
      restartSec += (extras * RESTART_COST_SEC) / group.count;
    }
  }
  const restartMin = restartSec / 60;
  const stretchMin = reinstructionMin + restartMin;

  // Spare Tire: wasted message cost
  const spareMin = conv.contextWaste
    ? (conv.contextWaste.wastedMessageCount * CONTEXT_WASTE_COST_SEC) / 60
    : 0;

  // Normalize each to 0–1
  const dragScore = Math.min(1, dragMin / DRAG_CEILING_MIN);
  const stretchScore = Math.min(1, stretchMin / STRETCH_CEILING_MIN);
  const spareScore = Math.min(1, spareMin / SPARE_CEILING_MIN);

  // Weighted composite
  const score = dragScore * WEIGHT_DRAG + stretchScore * WEIGHT_STRETCH + spareScore * WEIGHT_SPARE;

  return { score, dragScore, stretchScore, spareScore, dragMin, stretchMin, spareMin };
}

/**
 * Compute all tax components across a set of conversations.
 * Includes three-loss framework aliases.
 *
 * @param {ConversationAnalysis[]} conversations
 * @param {CrossChatAnalysis} crossChat
 * @returns {import('./types.js').TaxBreakdown}
 */
function computeTaxComponents(conversations, crossChat) {
  let renderOverheadSeconds = 0;
  for (const conv of conversations) {
    for (const tp of conv.turnPairs) {
      if (tp.perceivedWait) {
        renderOverheadSeconds += tp.perceivedWait.renderDelay;
      }
    }
  }
  const renderOverheadMinutes = renderOverheadSeconds / 60;

  let totalReinstructions = 0;
  for (const conv of conversations) {
    if (conv.reinstruction) {
      totalReinstructions += conv.reinstruction.reinstructionCount;
    }
  }
  const reinstructionMinutes = (totalReinstructions * REINSTRUCTION_COST_SEC) / 60;

  const totalRestartConversations = crossChat.topicRestarts.totalRestartConversations;
  const totalRestartGroups = crossChat.topicRestarts.totalRestartGroups;
  const extraRestarts = Math.max(0, totalRestartConversations - totalRestartGroups);
  const topicRestartMinutes = (extraRestarts * RESTART_COST_SEC) / 60;

  let totalWastedMessages = 0;
  for (const conv of conversations) {
    if (conv.contextWaste) {
      totalWastedMessages += conv.contextWaste.wastedMessageCount;
    }
  }
  const contextWasteMinutes = (totalWastedMessages * CONTEXT_WASTE_COST_SEC) / 60;

  const totalMinutes = renderOverheadMinutes + reinstructionMinutes + topicRestartMinutes + contextWasteMinutes;

  return {
    totalMinutes,
    totalHours: totalMinutes / 60,
    renderOverheadMinutes,
    reinstructionMinutes,
    reinstructionCount: totalReinstructions,
    topicRestartMinutes,
    topicRestartExtraConversations: extraRestarts,
    contextWasteMinutes,
    contextWasteCount: totalWastedMessages,
    // Three-loss framework aliases
    waitingLossMinutes: renderOverheadMinutes,
    cognitiveLossMinutes: reinstructionMinutes + topicRestartMinutes,
  };
}
